import * as React from "react";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { VscMarkdown } from "react-icons/vsc";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface Page {
  index: number;
  name: string;
  route: string;
}

interface Props {
  pages: {
    index: number;
    name: string;
    route: string;
  }[];
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  currentComponent: string;
  setCurrentComponent: React.Dispatch<React.SetStateAction<string>>;
  visiblePageIndexs: number[];
  setVisiblePageIndexs: React.Dispatch<React.SetStateAction<number[]>>;
}

export default function AppTree({
  pages,
  selectedIndex,
  setSelectedIndex,
  currentComponent,
  setCurrentComponent,
  visiblePageIndexs,
  setVisiblePageIndexs,
}: Props) {
  const navigate = useNavigate();
  const theme = useTheme();
  let { pathname } = useLocation();

  const page: Page = pages.find((x) => x.route === pathname)!;

  useEffect(() => {
    if (page) {
      setSelectedIndex(page.index);
    }
  }, [page, setSelectedIndex]);

  function renderItemBgColor(index: number) {
    if (theme.palette.mode === "dark") {
      return selectedIndex === index ? "rgba(144,202,249,0.16)" : "transparent";
    } else {
      return selectedIndex === index ? "#295fbf" : "transparent";
    }
  }

  function renderItemColor(index: number) {
    if (theme.palette.mode === "dark") {
      return selectedIndex === index && currentComponent === "tree"
        ? "white"
        : "#bdc3cf";
    } else {
      return selectedIndex === index ? "#e2ffff" : "#69665f";
    }
  }

  return (
    <Box sx={{ minWidth: 220 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pl: 1,
          py: 0.5,
          cursor: "pointer",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.04)",
          },
        }}
        onClick={() => {
          navigate("/");
          setSelectedIndex(-1);
        }}
      >
        <ExpandMoreIcon
          sx={{ fontSize: 16, color: theme.palette.mode === "dark" ? "#bdc3cf" : "#69665f" }}
        />
        <Typography
          variant="caption"
          sx={{
            ml: 0.5,
            fontSize: "0.8rem",
            color: theme.palette.mode === "dark" ? "#bdc3cf" : "#69665f",
          }}
        >
          Home
        </Typography>
      </Box>
      <List dense disablePadding>
        {pages.map(({ index, name, route }) => (
          <ListItemButton
            key={index}
            sx={{
              pl: 3,
              py: 0.2,
              backgroundColor: renderItemBgColor(index),
              color: renderItemColor(index),
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(144,202,249,0.08)"
                    : "rgba(0,0,0,0.04)",
              },
            }}
            onClick={() => {
              if (!visiblePageIndexs.includes(index)) {
                const newIndexs = [...visiblePageIndexs, index];
                setVisiblePageIndexs(newIndexs);
              }
              navigate(route);
              setSelectedIndex(index);
              setCurrentComponent("tree");
            }}
          >
            <ListItemIcon sx={{ minWidth: 24 }}>
              <VscMarkdown color="#6997d5" />
            </ListItemIcon>
            <ListItemText
              primary={name}
              primaryTypographyProps={{
                fontSize: "0.8rem",
                noWrap: true,
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
}
